import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Description } from '@mui/icons-material';
import PageBreadcrumb from '../components/PageBreadcrumb';

const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  type ContentType = {
    title: string;
    lastUpdated: string;
    sections: Array<{
      title: string;
      content: string[];
    }>;
  };

  const content: Record<string, ContentType> = {
    en: {
      title: 'Terms of Service',
      lastUpdated: 'Last Updated: January 2025',
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: [
            'By accessing and using Mr.Mirror®, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.',
          ],
        },
        {
          title: '2. Description of Service',
          content: [
            'Mr.Mirror® provides AI-powered script analysis services, including:',
            '• Script analysis and evaluation',
            '• Character breakdown generation',
            '• Script reading and voiceover services',
            '• Related tools and features',
            'We reserve the right to modify, suspend, or discontinue any aspect of the service at any time.',
          ],
        },
        {
          title: '3. User Accounts',
          content: [
            'To use our service, you must:',
            '• Provide accurate and complete registration information',
            '• Maintain the security of your account credentials',
            '• Be responsible for all activities under your account',
            '• Notify us immediately of any unauthorized use',
            'You must be at least 18 years old to use this service.',
          ],
        },
        {
          title: '4. User Responsibilities',
          content: [
            'You agree to:',
            '• Use the service only for lawful purposes',
            '• Not upload content that infringes on intellectual property rights',
            '• Not attempt to reverse engineer or compromise the service',
            '• Not use the service to generate harmful or illegal content',
            '• Respect the rights of other users',
          ],
        },
        {
          title: '5. Intellectual Property',
          content: [
            '• You retain ownership of content you upload',
            '• You grant us a license to use your content solely to provide the service',
            '• Our service, including AI models and algorithms, remain our intellectual property',
            '• You may not copy, modify, or distribute our service without permission',
          ],
        },
        {
          title: '6. Payment and Subscription',
          content: [
            '• Subscription fees are billed in advance',
            '• All fees are non-refundable except as required by law',
            '• We reserve the right to change pricing with 30 days notice',
            '• Failure to pay may result in service suspension',
            '• Token purchases are final and non-refundable',
          ],
        },
        {
          title: '7. Service Availability',
          content: [
            'We strive to maintain service availability but do not guarantee:',
            '• Uninterrupted or error-free service',
            '• Specific response times',
            '• Accuracy of AI-generated analyses',
            'We are not liable for any downtime or service interruptions.',
          ],
        },
        {
          title: '8. Limitation of Liability',
          content: [
            'To the maximum extent permitted by law:',
            '• We are not liable for indirect, incidental, or consequential damages',
            '• Our total liability is limited to the amount you paid in the last 12 months',
            '• We do not guarantee the accuracy of AI-generated content',
            '• You use the service at your own risk',
          ],
        },
        {
          title: '9. Termination',
          content: [
            'We may terminate or suspend your account if:',
            '• You violate these Terms of Service',
            '• You engage in fraudulent or illegal activity',
            '• You fail to pay required fees',
            'You may cancel your subscription at any time through your account settings.',
          ],
        },
        {
          title: '10. Changes to Terms',
          content: [
            'We reserve the right to modify these terms at any time. We will notify you of significant changes via email or through the service. Continued use after changes constitutes acceptance.',
          ],
        },
        {
          title: '11. Contact Information',
          content: [
            'For questions about these Terms of Service, please contact us through our contact form or email us at legal@mrmirror.ai',
          ],
        },
      ],
    },
    tr: {
      title: 'Kullanım Şartları',
      lastUpdated: 'Son Güncelleme: Ocak 2025',
      sections: [
        {
          title: '1. Şartların Kabulü',
          content: [
            'Mr.Mirror®\'ye erişerek ve kullanarak, bu Kullanım Şartlarına bağlı kalmayı kabul edersiniz. Bu şartları kabul etmiyorsanız, lütfen hizmetimizi kullanmayın.',
          ],
        },
        {
          title: '2. Hizmet Açıklaması',
          content: [
            'Mr.Mirror® şu AI destekli senaryo analiz hizmetlerini sağlar:',
            '• Senaryo analizi ve değerlendirme',
            '• Karakter dökümü oluşturma',
            '• Senaryo okuma ve seslendirme hizmetleri',
            '• İlgili araçlar ve özellikler',
            'Hizmetin herhangi bir yönünü istediğimiz zaman değiştirme, askıya alma veya durdurma hakkını saklı tutarız.',
          ],
        },
        {
          title: '3. Kullanıcı Hesapları',
          content: [
            'Hizmetimizi kullanmak için:',
            '• Doğru ve eksiksiz kayıt bilgileri sağlamalısınız',
            '• Hesap kimlik bilgilerinizin güvenliğini korumalısınız',
            '• Hesabınız altındaki tüm faaliyetlerden sorumlu olmalısınız',
            '• Yetkisiz kullanım durumunda derhal bizi bilgilendirmelisiniz',
            'Bu hizmeti kullanmak için en az 18 yaşında olmalısınız.',
          ],
        },
        {
          title: '4. Kullanıcı Sorumlulukları',
          content: [
            'Şunları kabul edersiniz:',
            '• Hizmeti yalnızca yasal amaçlar için kullanmak',
            '• Fikri mülkiyet haklarını ihlal eden içerik yüklememek',
            '• Hizmeti tersine mühendislik yapmaya veya tehlikeye atmaya çalışmamak',
            '• Hizmeti zararlı veya yasadışı içerik üretmek için kullanmamak',
            '• Diğer kullanıcıların haklarına saygı göstermek',
          ],
        },
        {
          title: '5. Fikri Mülkiyet',
          content: [
            '• Yüklediğiniz içeriğin sahipliğini siz korursunuz',
            '• İçeriğinizi yalnızca hizmeti sağlamak için kullanma lisansı veriyorsunuz',
            '• AI modelleri ve algoritmaları dahil hizmetimiz bizim fikri mülkiyetimizdir',
            '• İznimiz olmadan hizmetimizi kopyalayamaz, değiştiremez veya dağıtamazsınız',
          ],
        },
        {
          title: '6. Ödeme ve Abonelik',
          content: [
            '• Abonelik ücretleri önceden tahsil edilir',
            '• Tüm ücretler yasal olarak gerekli olmadıkça iade edilmez',
            '• 30 gün önceden bildirimle fiyatlandırmayı değiştirme hakkını saklı tutarız',
            '• Ödeme yapılmaması hizmetin askıya alınmasına neden olabilir',
            '• Token satın alımları kesindir ve iade edilmez',
          ],
        },
        {
          title: '7. Hizmet Kullanılabilirliği',
          content: [
            'Hizmet kullanılabilirliğini korumaya çalışıyoruz ancak şunları garanti etmiyoruz:',
            '• Kesintisiz veya hatasız hizmet',
            '• Belirli yanıt süreleri',
            '• AI tarafından oluşturulan analizlerin doğruluğu',
            'Herhangi bir kesinti veya hizmet kesintisinden sorumlu değiliz.',
          ],
        },
        {
          title: '8. Sorumluluk Sınırlaması',
          content: [
            'Yasaların izin verdiği maksimum ölçüde:',
            '• Dolaylı, tesadüfi veya sonuçsal zararlardan sorumlu değiliz',
            '• Toplam sorumluluğumuz son 12 ayda ödediğiniz tutarla sınırlıdır',
            '• AI tarafından oluşturulan içeriğin doğruluğunu garanti etmiyoruz',
            '• Hizmeti kendi riskiniz altında kullanırsınız',
          ],
        },
        {
          title: '9. Fesih',
          content: [
            'Hesabınızı şu durumlarda feshedebilir veya askıya alabiliriz:',
            '• Bu Kullanım Şartlarını ihlal ederseniz',
            '• Dolandırıcılık veya yasadışı faaliyetlerde bulunursanız',
            '• Gerekli ücretleri ödemezseniz',
            'Aboneliğinizi hesap ayarlarınızdan istediğiniz zaman iptal edebilirsiniz.',
          ],
        },
        {
          title: '10. Şartlardaki Değişiklikler',
          content: [
            'Bu şartları istediğimiz zaman değiştirme hakkını saklı tutarız. Önemli değişiklikleri e-posta veya hizmet aracılığıyla size bildireceğiz. Değişikliklerden sonra kullanıma devam etmek kabul anlamına gelir.',
          ],
        },
        {
          title: '11. İletişim Bilgileri',
          content: [
            'Bu Kullanım Şartları hakkında sorularınız için lütfen iletişim formumuz aracılığıyla bizimle iletişime geçin veya legal@mrmirror.ai adresine e-posta gönderin',
          ],
        },
      ],
    },
  };

  const terms: ContentType = content[language] || content.en;
  const isTr = language === 'tr';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/02</span> · {isTr ? 'YASAL' : 'LEGAL'}</span>
          <span className="eyebrow-mute hidden md:inline">{terms.lastUpdated.toUpperCase()}</span>
        </div>

        <div className="mb-16 pb-12 border-b border-hairline">
          <h1 className="display-h display-lg mb-4">{terms.title}</h1>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-mute">
            {terms.lastUpdated}
          </p>
        </div>

        <article className="space-y-14">
          {terms.sections.map((section, index) => (
            <section key={index} className="grid grid-cols-12 gap-6 md:gap-10">
              <div className="col-span-12 md:col-span-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent block mb-2">
                  /{String(index + 1).padStart(2, '0')}
                </span>
                <h2 className="font-serif italic text-xl md:text-2xl text-ink leading-tight">
                  {section.title.replace(/^\d+\.\s*/, '')}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-9">
                <ul className="space-y-3 font-prose text-[15px] leading-relaxed text-ink-dim">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;

